import React, { useState, useEffect, useRef } from "react";
import html2canvas from "html2canvas";
import { fmt } from "../../utils/fmt";
import api from "../../utils/api";
import {
  StatCard,
  DataTable,
  PageHeader,
  Button,
  Modal,
  FormInput,
  StatusBadge,
  PrintButton,
  PrintHeader,
} from "../../components/common";
import toast from "react-hot-toast";

// ═══ Receipt Image Modal ═══
function ReceiptImageModal({ transaction, onClose }) {
  const receiptRef = useRef(null);
  if (!transaction) return null;

  const handleSaveImage = async () => {
    if (!receiptRef.current) return;
    try {
      const canvas = await html2canvas(receiptRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
      });
      const url = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = url;
      a.download = `receipt-${transaction.id}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success("Image saved!");
    } catch (e) {
      toast.error("Save failed.");
    }
  };

  const handleShare = async () => {
    if (!receiptRef.current) return;
    try {
      const canvas = await html2canvas(receiptRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
      });
      canvas.toBlob(async (blob) => {
        const file = new File([blob], `receipt-${transaction.id}.png`, {
          type: "image/png",
        });
        if (
          navigator.share &&
          navigator.canShare &&
          navigator.canShare({ files: [file] })
        ) {
          await navigator.share({
            files: [file],
            title: `Receipt #${transaction.id}`,
          });
        } else {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `receipt-${transaction.id}.png`;
          a.click();
          toast.success("Image saved! Share from gallery.");
        }
      }, "image/png");
    } catch (e) {
      toast.error("Share failed.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-sm w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div ref={receiptRef}>
          <div
            style={{
              background: "#1a1a2e",
              padding: "20px 24px",
              borderRadius: "16px 16px 0 0",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <div
                  style={{
                    color: "#fff",
                    fontSize: "20px",
                    fontWeight: "bold",
                  }}
                >
                  SS PAY
                </div>
                <div
                  style={{
                    color: "#aeaeb2",
                    fontSize: "12px",
                    marginTop: "2px",
                  }}
                >
                  Payment Receipt
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ color: "#fff", fontSize: "13px" }}>
                  #{transaction.id}
                </div>
                <div
                  style={{
                    color: "#34d399",
                    fontSize: "11px",
                    fontWeight: "bold",
                  }}
                >
                  {transaction.status}
                </div>
              </div>
            </div>
          </div>
          <div
            style={{
              background: "#f0fdf4",
              padding: "16px 24px",
              textAlign: "center",
            }}
          >
            <div style={{ color: "#166534", fontSize: "12px" }}>Amount</div>
            <div
              style={{ color: "#166534", fontSize: "28px", fontWeight: "bold" }}
            >
              ₹{parseFloat(transaction.amount).toLocaleString()}
            </div>
          </div>
          <div style={{ padding: "16px 24px" }}>
            {[
              ["Type", transaction.transactionType],
              ["UPI ID", transaction.upiId || "-"],
              ["Account", transaction.accountNumber || "-"],
              ["IFSC", transaction.ifscCode || "-"],
              ["Holder", transaction.accountHolderName || "-"],
              ["UTR Number", transaction.utrNumber || "-"],
              ["Remark", transaction.notes || "-"],
              ["Created", new Date(transaction.createdAt).toLocaleString()],
              [
                "Cleared",
                transaction.transactionClearTime
                  ? new Date(transaction.transactionClearTime).toLocaleString()
                  : "-",
              ],
            ].map(([label, value]) => (
              <div
                key={label}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "6px 0",
                  borderBottom: "1px solid #f3f4f6",
                  fontSize: "12px",
                }}
              >
                <span style={{ color: "#9ca3af" }}>{label}</span>
                <span
                  style={{
                    color: "#1f2937",
                    fontWeight: "600",
                    maxWidth: "60%",
                    textAlign: "right",
                    wordBreak: "break-all",
                  }}
                >
                  {value}
                </span>
              </div>
            ))}
          </div>
          <div
            style={{
              padding: "12px 24px 20px",
              textAlign: "center",
              borderTop: "1px solid #e5e7eb",
            }}
          >
            <div style={{ color: "#aeaeb2", fontSize: "10px" }}>
              System generated receipt
            </div>
            <div style={{ color: "#aeaeb2", fontSize: "10px" }}>
              © 2026 SS PAY
            </div>
          </div>
        </div>
        <div className="flex gap-2 p-4 border-t border-gray-100">
          <button
            onClick={handleSaveImage}
            className="flex-1 h-11 bg-emerald-500 text-white text-sm font-semibold rounded-xl hover:bg-emerald-600"
          >
            Save Image
          </button>
          <button
            onClick={handleShare}
            className="flex-1 h-11 bg-blue-500 text-white text-sm font-semibold rounded-xl hover:bg-blue-600"
          >
            Share
          </button>
          <button
            onClick={onClose}
            className="h-11 px-4 bg-gray-100 text-gray-600 text-sm font-semibold rounded-xl hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══ QR Code generator ═══
function QRCodeCanvas({ value, size = 180 }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    if (!canvasRef.current || !value) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    canvas.width = size;
    canvas.height = size;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(value)}`;
    img.onload = () => {
      ctx.drawImage(img, 0, 0, size, size);
    };
    img.onerror = () => {
      ctx.fillStyle = "#f3f4f6";
      ctx.fillRect(0, 0, size, size);
      ctx.fillStyle = "#9ca3af";
      ctx.font = "12px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("QR Code", size / 2, size / 2);
    };
  }, [value, size]);
  return <canvas ref={canvasRef} style={{ width: size, height: size }} />;
}

// ═══ Account Details Modal ═══
function AccountDetailsModal({ transaction, onClose }) {
  if (!transaction) return null;
  const isUPI = transaction.transactionType === "UPI";
  const qrValue = isUPI
    ? `upi://pay?pa=${transaction.upiId}&pn=Payment&am=${transaction.amount}&cu=INR`
    : null;
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success("Copied!");
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 text-lg"
        >
          ×
        </button>
        <h2 className="text-base font-semibold text-gray-800 mb-4">
          Account Details
        </h2>
        {isUPI ? (
          <div className="flex flex-col items-center gap-3">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-500 font-medium">UPI ID:</span>
              <span className="font-semibold text-gray-800">
                {transaction.upiId}
              </span>
              <button
                onClick={() => handleCopy(transaction.upiId)}
                className="text-blue-500 hover:text-blue-600 ml-1"
                title="Copy UPI ID"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-4 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
              </button>
            </div>
            <div className="text-sm text-gray-700">
              <span className="font-medium">Amount</span>:{" "}
              <span className="font-semibold">
                {parseFloat(transaction.amount).toLocaleString()}
              </span>
            </div>
            <div className="border rounded-xl overflow-hidden p-2 bg-white shadow-sm">
              <QRCodeCanvas value={qrValue} size={180} />
            </div>
          </div>
        ) : (
          <div className="text-sm space-y-2">
            <div>
              <span className="font-medium text-gray-500">Bank:</span>{" "}
              {transaction.bankName}
            </div>
            <div>
              <span className="font-medium text-gray-500">Account:</span>{" "}
              {transaction.accountNumber}
            </div>
            <div>
              <span className="font-medium text-gray-500">IFSC:</span>{" "}
              {transaction.ifscCode}
            </div>
            <div>
              <span className="font-medium text-gray-500">Holder:</span>{" "}
              {transaction.accountHolderName}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══ Operator Dashboard ═══
export function OperatorDashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/operator/dashboard").then((r) => setStats(r.data.data));
  }, []);

  return (
    <div>
      <PageHeader title="Operator Dashboard" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          title="Total Transfer Amount"
          value={stats?.totalTransferAmount || 0}
          index={0}
        />
        <StatCard
          title="Total Pending Amount"
          value={stats?.totalPendingAmount || 0}
          index={1}
        />
        <StatCard
          title="Total Payment Lunga"
          value={stats?.totalPaymentLunga || 0}
          index={2}
        />
        <StatCard
          title="Pending Count"
          value={stats?.totalPendingCount || 0}
          prefix=""
          index={3}
        />
        <StatCard
          title="Cleared Count"
          value={stats?.totalTransferCount || 0}
          prefix=""
          index={4}
        />
        <StatCard
          title="Total AED Lunga"
          value={stats?.totalAedLunga || 0}
          prefix="AED "
          index={5}
        />
        <StatCard
          title="Total USDT Lunga"
          value={stats?.totalUsdtLunga || 0}
          prefix="USDT "
          index={0}
        />
        <StatCard
          title="Available Details"
          value={stats?.availableLimit || 0}
          index={6}
        />
        <StatCard
          title="Agent Commission Amount"
          value={stats?.agentCommissionAmount || 0}
          index={1}
        />
      </div>
    </div>
  );
}

// ═══ Operator Transactions ═══
export function OperatorTransactions() {
  const [transactions, setTransactions] = useState([]);
  const [pending, setPending] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showPay, setShowPay] = useState(null);
  const [showUtr, setShowUtr] = useState(null);
  const [showAccountDetails, setShowAccountDetails] = useState(null);
  const [showReceipt, setShowReceipt] = useState(null);
  const [utr, setUtr] = useState("");
  const [proof, setProof] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [budgetInput, setBudgetInput] = useState("");
  const [pendingLoading, setPendingLoading] = useState(false);

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const handleBulkPick = async () => {
    if (!selectedIds.length) {
      toast.error("Select transactions first.");
      return;
    }
    try {
      const r = await api.post("/operator/transactions/bulk-pick", {
        transactionIds: selectedIds,
      });
      toast.success(r.data.message);
      setSelectedIds([]);
      setPending([]);
      setBudgetInput("");
      fetchData();
    } catch (e) {
      toast.error("Error.");
    }
  };

  const handleExportPicked = async () => {
    try {
      const r = await api.get("/operator/transactions/export-picked", {
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "picked-transactions.xlsx";
      a.click();
      toast.success("Exported! Fill UTR column and re-upload.");
    } catch (e) {
      toast.error("Export failed.");
    }
  };

  const handleBulkClear = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const r = await api.post("/operator/transactions/bulk-clear", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(r.data.message);
      fetchData();
    } catch (err) {
      toast.error("Upload failed.");
    }
    e.target.value = "";
  };

  const fetchData = () => {
    setLoading(true);
    api.get(`/operator/transactions?page=${page}&limit=10`).then((r) => {
      setTransactions(r.data.data);
      setTotal(r.data.total);
      setLoading(false);
    });
  };

  const fetchPending = async () => {
    const budget = parseFloat(budgetInput);
    if (!budget || budget <= 0) {
      toast.error("Enter a valid amount.");
      return;
    }
    setPendingLoading(true);
    try {
      const r = await api.get(`/operator/pending-transactions?amount=${budget}`);
      setPending(r.data.data || []);
      if ((r.data.data || []).length === 0) {
        toast("No transactions available within this budget.", { icon: "ℹ️" });
      }
    } catch (e) {
      toast.error("Failed to fetch.");
    }
    setPendingLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [page]);

  const handlePick = async (id) => {
    try {
      await api.post(`/operator/transactions/${id}/pick`);
      toast.success("Transaction picked");
      // Remove from pending list
      setPending((prev) => prev.filter((t) => t.id !== id));
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.message || "Error picking transaction");
    }
  };

  const handlePay = async () => {
    try {
      await api.post(`/operator/transactions/${showPay.id}/pay`);
      toast.success("Payment marked as PAID");
      setShowPay(null);
      fetchData();
    } catch (e) {
      toast.error("Payment error");
    }
  };

  const handleClear = async () => {
    if (!utr) {
      toast.error("UTR required");
      return;
    }
    try {
      const formData = new FormData();
      formData.append("utrNumber", utr);
      if (proof) formData.append("proof", proof);
      await api.post(`/operator/transactions/${showUtr.id}/clear`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Transaction cleared");
      setShowUtr(null);
      setUtr("");
      setProof(null);
      fetchData();
    } catch (e) {
      toast.error("Error clearing transaction");
    }
  };

  const handleReject = async (id) => {
    const reason = window.prompt("Enter reject reason:");
    try {
      await api.post(`/operator/transactions/${id}/reject`, { reason });
      toast.success("Transaction rejected");
      fetchData();
    } catch (e) {
      toast.error("Error rejecting transaction");
    }
  };

  const pendingTotal = pending.reduce((s, t) => s + parseFloat(t.amount), 0);

  return (
    <div>
      {/* ── Budget Fetch Section ── */}
      <div className="mb-6 p-4 bg-white border border-gray-200 rounded-2xl shadow-sm">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">
              Enter Amount Budget (₹)
            </label>
            <input
              type="number"
              value={budgetInput}
              onChange={(e) => setBudgetInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchPending()}
              placeholder="e.g. 100000"
              className="h-9 px-3 text-sm border border-gray-200 rounded-lg outline-none focus:border-brand-500 w-52"
            />
          </div>
          <Button
            onClick={fetchPending}
            variant="primary"
            className="h-9 px-5 text-sm"
            disabled={pendingLoading}
          >
            {pendingLoading ? "Fetching..." : "Fetch Available"}
          </Button>
          {pending.length > 0 && (
            <div className="flex items-center gap-3 ml-auto text-sm">
              <span className="text-gray-500">
                <span className="font-semibold text-gray-800">{pending.length}</span> transactions
              </span>
              <span className="text-gray-400">·</span>
              <span className="text-gray-500">
                Total: <span className="font-semibold text-brand-600">₹{pendingTotal.toLocaleString()}</span>
              </span>
              <button
                onClick={() => { setPending([]); setSelectedIds([]); }}
                className="text-xs text-red-400 hover:text-red-600 underline"
              >
                Clear
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Available Transactions ── */}
      {pending.length > 0 && (
        <div className="mb-6">
          <PageHeader
            title="Available Transactions"
            subtitle="Pick a transaction to process"
          />
          <DataTable
            columns={[
              {
                header: "",
                render: (r) => (
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(r.id)}
                    onChange={() => toggleSelect(r.id)}
                    className="w-4 h-4 rounded border-gray-300 text-brand-500"
                  />
                ),
              },
              { header: "ID", key: "id" },
              {
                header: "Amount",
                render: (r) => `₹${parseFloat(r.amount).toLocaleString()}`,
              },
              { header: "Type", key: "transactionType" },
              {
                header: "Created",
                render: (r) => new Date(r.createdAt).toLocaleString(),
              },
              // ⚠️ No UPI ID, No Account Number — hidden until picked
            ]}
            data={pending}
            total={pending.length}
            page={1}
            actions={(r) => (
              <Button
                onClick={() => handlePick(r.id)}
                variant="primary"
                className="h-8 px-3 text-xs"
              >
                Pick
              </Button>
            )}
          />
          {selectedIds.length > 0 && (
            <div className="mt-3 flex items-center gap-3 p-3 bg-blue-50 rounded-xl">
              <span className="text-sm text-blue-700 font-medium">
                {selectedIds.length} selected
              </span>
              <Button
                onClick={handleBulkPick}
                variant="primary"
                className="h-8 px-3 text-xs"
              >
                Bulk Pick Selected
              </Button>
            </div>
          )}
        </div>
      )}

      {/* ── Transactions List ── */}
      <div className="flex items-center justify-between mb-4">
        <PageHeader title="Transactions List" />
        <div className="flex gap-2">
          <Button
            onClick={handleExportPicked}
            variant="primary"
            className="h-9 px-3 text-xs bg-emerald-600 hover:bg-emerald-700"
          >
            Export Picked 📥
          </Button>
          <label className="h-9 px-3 bg-brand-500 text-white text-xs font-semibold rounded-xl inline-flex items-center justify-center cursor-pointer hover:bg-brand-600 transition-mac">
            Upload Cleared 📤
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleBulkClear}
              className="hidden"
            />
          </label>
        </div>
      </div>

      <DataTable
        columns={[
          { header: "ID", key: "id" },
          {
            header: "Amount",
            render: (r) => `₹${parseFloat(r.amount).toLocaleString()}`,
          },
          { header: "UTR Number", render: (r) => r.utrNumber || "-" },
          { header: "Notes", render: (r) => r.notes || "-" },
          {
            header: "Created",
            render: (r) => new Date(r.createdAt).toLocaleString(),
          },
          {
            header: "Cleared/Rejected",
            render: (r) =>
              r.transactionClearTime
                ? new Date(r.transactionClearTime).toLocaleString()
                : "-",
          },
          {
            header: "Status",
            render: (r) => <StatusBadge status={r.status} />,
          },
          {
            header: "View",
            render: (r) => (
              <button
                onClick={() => setShowAccountDetails(r)}
                className="text-blue-500 text-xs underline hover:text-blue-700"
              >
                Account Details
              </button>
            ),
          },
        ]}
        data={transactions}
        total={total}
        page={page}
        onPageChange={setPage}
        loading={loading}
        actions={(r) => (
          <div className="flex gap-1">
            {r.status === "PICKED" && (
              <Button
                onClick={() => setShowPay(r)}
                variant="primary"
                className="h-7 px-2 text-xs"
              >
                Pay
              </Button>
            )}
            {r.status === "PAID" && (
              <Button
                onClick={() => setShowUtr(r)}
                variant="primary"
                className="h-7 px-2 text-xs"
              >
                Submit UTR
              </Button>
            )}
            {["PICKED", "PAID"].includes(r.status) && (
              <Button
                onClick={() => handleReject(r.id)}
                variant="danger"
                className="h-7 px-2 text-xs"
              >
                Reject
              </Button>
            )}
            {r.status === "CLEARED" && (
              <button
                onClick={() => setShowReceipt(r)}
                className="text-emerald-600 text-sm font-medium py-2 px-3 min-h-[40px] bg-emerald-50 rounded-lg hover:bg-emerald-100 inline-flex items-center"
              >
                Receipt
              </button>
            )}
          </div>
        )}
      />

      {/* Modals — unchanged */}
      {showReceipt && (
        <ReceiptImageModal
          transaction={showReceipt}
          onClose={() => setShowReceipt(null)}
        />
      )}
      {showAccountDetails && (
        <AccountDetailsModal
          transaction={showAccountDetails}
          onClose={() => setShowAccountDetails(null)}
        />
      )}

      <Modal open={!!showPay} onClose={() => setShowPay(null)} title="Make Payment">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Transaction #{showPay?.id} — ₹{showPay && parseFloat(showPay.amount).toLocaleString()}
          </p>
          {showPay?.transactionType === "UPI" && (
            <div className="bg-gray-50 p-4 rounded text-center">
              <p className="text-sm text-gray-600 mb-2">Scan to Pay</p>
              <div className="flex justify-center mb-3">
                <QRCodeCanvas
                  value={`upi://pay?pa=${showPay.upiId}&pn=Payment&am=${showPay.amount}&cu=INR`}
                  size={180}
                />
              </div>
              <p className="text-sm font-semibold">{showPay.upiId}</p>
              <p className="text-sm text-gray-500">
                Amount: ₹{parseFloat(showPay.amount).toLocaleString()}
              </p>
            </div>
          )}
          {showPay?.transactionType === "BANK_ACCOUNT" && (
            <div className="bg-gray-50 p-3 rounded text-sm space-y-1">
              <p><b>Bank:</b> {showPay.bankName}</p>
              <p><b>Account:</b> {showPay.accountNumber}</p>
              <p><b>IFSC:</b> {showPay.ifscCode}</p>
              <p><b>Holder:</b> {showPay.accountHolderName}</p>
            </div>
          )}
          <Button onClick={handlePay} className="w-full">Mark as Paid</Button>
        </div>
      </Modal>

      <Modal open={!!showUtr} onClose={() => setShowUtr(null)} title="Submit Payment">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Transaction #{showUtr?.id} — ₹{showUtr && parseFloat(showUtr.amount).toLocaleString()}
          </p>
          <FormInput
            label="UTR Number"
            required
            value={utr}
            onChange={(e) => setUtr(e.target.value)}
            placeholder="Enter UTR Number"
          />
          <div>
            <label className="text-sm text-gray-700">Upload Payment Proof</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setProof(e.target.files[0])}
              className="mt-1 block w-full text-sm"
            />
          </div>
          <Button onClick={handleClear} className="w-full">Submit Payment</Button>
        </div>
      </Modal>
    </div>
  );
}

export function OperatorLedger() {
  const [transactions, setTransactions] = useState([]);
  const [rates, setRates] = useState({ aedTodayRate: 1 });
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState("");

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (selectedDate) {
      params.startDate = selectedDate;
      params.endDate = selectedDate;
    }
    Promise.all([
      api.get("/operator/ledger", { params }),
      api.get("/config/current-rates"),
    ])
      .then(([txRes, rateRes]) => {
        setTransactions(txRes.data.data || []);
        const r = rateRes.data.data?.[0];
        if (r) setRates({ aedTodayRate: parseFloat(r.aedTodayRate || 1) });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [selectedDate]);

  const { aedTodayRate } = rates;
  const toAed = (inr) => (aedTodayRate > 0 ? inr / aedTodayRate : 0);

  const grandINR = transactions.reduce(
    (s, tx) => s + parseFloat(tx.amount || 0),
    0,
  );
  const grandComm = transactions.reduce(
    (s, tx) => s + parseFloat(tx.operatorCommission || 0),
    0,
  );
  const grandNet = grandINR - grandComm;

  const dateLabel = selectedDate
    ? new Date(selectedDate)
        .toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "2-digit",
        })
        .replace(/\//g, "-")
    : "All";

  if (loading) return <div className="p-6 text-gray-400">Loading...</div>;

  return (
    <div className="printable">
      <PrintHeader title="Ledger" subtitle={`Operator daily ledger · Date: ${selectedDate || "All dates"}`} />
      <div className="no-print flex items-center justify-between mb-4">
        <PageHeader title="Ledger" />
        <div className="flex items-end gap-2">
          <PrintButton />
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">
              Select Date
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="h-9 px-3 text-sm border border-gray-200 rounded-lg outline-none focus:border-brand-500 transition-mac"
            />
          </div>
          {selectedDate && (
            <button
              onClick={() => setSelectedDate("")}
              className="h-9 px-3 text-sm text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="bg-brand-500 text-white text-center py-3 rounded-t-xl font-bold text-lg">
        DAILY LEDGER ({dateLabel})
      </div>

      <div className="overflow-x-auto border border-gray-200 rounded-b-xl">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-brand-50">
              <th className="border border-gray-300 px-3 py-2 text-center font-semibold text-gray-700 w-12">
                SR.
              </th>
              <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700">
                NAME
              </th>
              <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700">
                A/C NO.
              </th>
              <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700">
                IFSC
              </th>
              <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700">
                UTR
              </th>
              <th className="border border-gray-300 px-3 py-2 text-right font-semibold text-gray-700">
                RATE
              </th>
              <th className="border border-gray-300 px-3 py-2 text-right font-semibold text-gray-700">
                AMOUNT (INR)
              </th>
              <th className="border border-gray-300 px-3 py-2 text-right font-semibold text-gray-700">
                COMMISSION
              </th>
              <th className="border border-gray-300 px-3 py-2 text-right font-semibold text-gray-700">
                NET (INR)
              </th>
              <th className="border border-gray-300 px-3 py-2 text-right font-semibold text-gray-700">
                NET (AED)
              </th>
            </tr>
          </thead>
          <tbody>
            {transactions.length === 0 ? (
              <tr>
                <td
                  colSpan={10}
                  className="border border-gray-200 px-3 py-6 text-center text-gray-400"
                >
                  No cleared transactions{selectedDate ? " for this date" : ""}.
                </td>
              </tr>
            ) : (
              transactions.map((tx, idx) => {
                const inr = parseFloat(tx.amount || 0);
                const comm = parseFloat(tx.operatorCommission || 0);
                const net = inr - comm;
                return (
                  <tr
                    key={tx.id}
                    className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    <td className="border border-gray-200 px-3 py-1.5 text-center text-gray-500">
                      {idx + 1}
                    </td>
                    <td className="border border-gray-200 px-3 py-1.5 text-gray-800">
                      {tx.accountHolderName || tx.notes || "-"}
                    </td>
                    <td className="border border-gray-200 px-3 py-1.5 text-gray-600">
                      {tx.accountNumber || "-"}
                    </td>
                    <td className="border border-gray-200 px-3 py-1.5 text-gray-600">
                      {tx.ifscCode || "-"}
                    </td>
                    <td className="border border-gray-200 px-3 py-1.5 text-gray-600">
                      {tx.utrNumber || "-"}
                    </td>
                    <td className="border border-gray-200 px-3 py-1.5 text-right text-gray-500">
                      {aedTodayRate}
                    </td>
                    <td className="border border-gray-200 px-3 py-1.5 text-right font-medium">
                      ₹{fmt(inr)}
                    </td>
                    <td className="border border-gray-200 px-3 py-1.5 text-right text-red-600 font-medium">
                      ₹{fmt(comm)}
                    </td>
                    <td className="border border-gray-200 px-3 py-1.5 text-right font-semibold text-green-700">
                      ₹{fmt(net)}
                    </td>
                    <td className="border border-gray-200 px-3 py-1.5 text-right font-medium text-green-600">
                      {fmt(toAed(net))}
                    </td>
                  </tr>
                );
              })
            )}
            <tr className="bg-brand-500 text-white font-bold">
              <td
                className="border border-gray-300 px-3 py-2 text-center"
                colSpan={6}
              >
                TOTAL
              </td>
              <td className="border border-gray-300 px-3 py-2 text-right">
                ₹{fmt(grandINR)}
              </td>
              <td className="border border-gray-300 px-3 py-2 text-right">
                ₹{fmt(grandComm)}
              </td>
              <td className="border border-gray-300 px-3 py-2 text-right">
                ₹{fmt(grandNet)}
              </td>
              <td className="border border-gray-300 px-3 py-2 text-right">
                {fmt(toAed(grandNet))}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex flex-wrap gap-4 text-sm">
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
          <span className="text-gray-500">Total INR:</span>
          <span className="ml-2 font-bold text-gray-800">₹{fmt(grandINR)}</span>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          <span className="text-gray-500">Commission:</span>
          <span className="ml-2 font-bold text-red-700">₹{fmt(grandComm)}</span>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3">
          <span className="text-gray-500">Net INR:</span>
          <span className="ml-2 font-bold text-green-700">
            ₹{fmt(grandNet)}
          </span>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3">
          <span className="text-gray-500">Net AED:</span>
          <span className="ml-2 font-bold text-green-700">
            {fmt(toAed(grandNet))}
          </span>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
          <span className="text-gray-500">Transactions:</span>
          <span className="ml-2 font-bold">{transactions.length}</span>
        </div>
      </div>
    </div>
  );
}
